import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FeaturesProvider } from "@/context/FeaturesContext";

import Landing from "@/pages/Landing";
import SearchResults from "@/pages/SearchResults";
import EstimatePage from "@/pages/EstimatePage";
import GoodsPage from "@/pages/GoodsPage";
import HowItWorks from "@/pages/HowItWorks";
import { WhatsAppFloat } from "@/components/WhatsApp";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import CustomerDashboard from "@/pages/CustomerDashboard";
import NotificationsPage from "@/pages/NotificationsPage";
import ShipmentDetail from "@/pages/ShipmentDetail";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ShopShip from "@/pages/ShopShip";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import MobileTabBar from "@/components/MobileTabBar";

function DashboardRouter() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "super_admin") return <AdminDashboard />;
  if (user.role === "partner") return <PartnerDashboard />;
  return <CustomerDashboard />;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/goods" element={<GoodsPage />} />
      <Route path="/estimate" element={<EstimatePage />} />
      <Route path="/shopship" element={<ShopShip />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/dashboard" element={<DashboardRouter />} />
      <Route path="/shipments/:id" element={<Protected roles={["customer", "admin", "super_admin"]}><ShipmentDetail /></Protected>} />
      <Route path="/notifications" element={<Protected roles={["customer", "partner", "admin", "super_admin"]}><NotificationsPage /></Protected>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <FeaturesProvider>
              <AppRouter />
              <WhatsAppFloat />
              <MobileTabBar />
              <Toaster position="top-right" richColors />
            </FeaturesProvider>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
